// components/OgrenciTableRow.js - ULTRA PERFORMANS OPTİMİZE EDİLMİŞ
import React, { memo, useMemo, useState, useCallback } from 'react';

// Teacher badge component - ayrı memoize edilmiş
const TeacherBadge = memo(({ info, onToggleStatus }) => {
  const isPassive = info.status === 0;
  const canToggleStatus = info.canToggleStatus !== false;
  const showPassiveState = isPassive || !canToggleStatus;

  // Son ders bilgisini daha anlamlı hale getir
  const getLastLessonDisplay = () => {
    if (info.dayCount === null || info.dayCount === undefined || isNaN(info.dayCount)) {
      return { text: 'Henüz ders yok', icon: '❌' };
    }
    // dayCount zaten pozitif
    const dayCount = info.dayCount;
    if (dayCount === 0) return { text: 'Bugün', icon: '✅' };
    if (dayCount === 1) return { text: 'Dün', icon: '✅' };
    if (dayCount <= 3) return { text: `${dayCount} gün önce`, icon: '✓' };
    if (dayCount <= 7) return { text: `${dayCount} gün önce`, icon: '📅' };
    if (dayCount <= 14) return { text: `${dayCount} gün önce`, icon: '⚠️' };
    if (dayCount <= 30) return { text: `${dayCount} gün önce`, icon: '⚠️' };
    return { text: `${dayCount} gün önce`, icon: '❌' };
  };

  const lessonInfo = getLastLessonDisplay();

  // Renk sınıfını belirle
  const getBadgeColorClass = () => {
    // Pasif öğretmen için kırmızı renk
    if (showPassiveState) {
      return 'bg-red-100 border-red-300';
    }

    if (info.dayCount === null || info.dayCount === undefined || isNaN(info.dayCount)) {
      return 'bg-red-50 border-red-400';
    }
    if (info.dayCount === 0) return 'bg-green-50 border-green-400';
    if (info.dayCount === 1) return 'bg-emerald-50 border-emerald-400';
    if (info.dayCount <= 7) return 'bg-blue-50 border-blue-400';
    if (info.dayCount <= 30) return 'bg-amber-50 border-amber-400';
    return 'bg-red-50 border-red-400';
  };

  return (
    <div className="inline-flex items-center gap-2 group">
      <div className={`inline-flex flex-col px-3 py-1.5 rounded-lg border-2 ${getBadgeColorClass()} shadow-sm hover:shadow-md transition-all`}>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-800">
            {info.name || 'İsimsiz Öğretmen'}
          </span>
          {showPassiveState && (
            <span className="px-1.5 py-0.5 bg-red-600 text-white rounded text-[10px] font-bold">P</span>
          )}
          {!showPassiveState && (
            <span className="px-1.5 py-0.5 bg-green-600 text-white rounded text-[10px] font-bold">A</span>
          )}
        </div>
        <div className="flex items-center gap-1 mt-0.5">
          <span className="text-xs">{lessonInfo.icon}</span>
          <span className="text-[10px] text-gray-600">
            {lessonInfo.text}
          </span>
        </div>
      </div>
      {canToggleStatus ? (
      <button
        onClick={(e) => {
          e.stopPropagation();
          onToggleStatus(info.teacherId);
        }}
        className={`flex items-center justify-center gap-1 px-2 py-1 rounded-md text-xs font-semibold transition-all shadow-md ${
          isPassive
            ? 'bg-green-600 text-white hover:bg-green-700'
            : 'bg-red-600 text-white hover:bg-red-700'
        }`}
        title={isPassive ? 'Öğretmeni aktif yap' : 'Öğretmeni pasif yap'}
      >
        {isPassive ? (
          <>
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>Aktif Et</span>
          </>
        ) : (
          <>
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span>Pasif Et</span>
          </>
        )}
      </button>
      ) : (
        <span
          className="inline-flex items-center px-2 py-1 rounded-md text-[11px] font-semibold bg-gray-200 text-gray-600"
          title="Ders kaydı oluşmadan aktif edilemez"
        >
          Pasif
        </span>
      )}
    </div>
  );
});
TeacherBadge.displayName = 'TeacherBadge';

// Ana row component
const OgrenciTableRow = memo(({
  ogrenci,
  isSelected,
  isExpanded,
  onSelectChange,
  onEdit,
  onDelete,
  onToggleExpand,
  onShowPastLessons,
  onShowPayments,
  onShowBalanceDetail,
  onToggleTeacherStatus, // Öğretmen status toggle handler
  getSubjectNames,
  getTeacherNames,
  getCompanyName,
  statusFilter,
  teachers // Teachers listesini de alalım
}) => {
  // Local state for hover effects
  const [isHovered, setIsHovered] = useState(false);
  const [showAllTeachers, setShowAllTeachers] = useState(false);
  
  // Telefon numarasını formatla
  const formatPhone = useCallback((phone) => {
    if (!phone) return '-';
    // Sadece rakamları al
    const numbers = phone.replace(/\D/g, '');
    // 10 haneli ise formatla
    if (numbers.length === 10) {
      return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)} ${numbers.slice(6, 8)} ${numbers.slice(8, 10)}`;
    } else if (numbers.length === 11 && numbers[0] === '0') {
      // 0 ile başlıyorsa
      return `(${numbers.slice(1, 4)}) ${numbers.slice(4, 7)} ${numbers.slice(7, 9)} ${numbers.slice(9, 11)}`;
    }
    return phone; // Formatlanamıyorsa olduğu gibi döndür
  }, []);
  
  // Memoize teacher infos with optimized calculation
  const teacherInfos = useMemo(() => {
    let teacherNames = [];
    let teacherIds = [];
    let teacherIdStrings = ogrenci.teacher_id_strings || [];
    
    // ÖNCELİKLE: teacher_name field'ını kontrol et (Backend'den isimler geliyor)
    if (ogrenci.teacher_name && Array.isArray(ogrenci.teacher_name) && ogrenci.teacher_name.length > 0) {
      // Backend'den gelen isimleri direkt kullan
      teacherNames = ogrenci.teacher_name;
      // teacher_ids de varsa onları da al
      if (ogrenci.teacher_ids && Array.isArray(ogrenci.teacher_ids)) {
        teacherIds = ogrenci.teacher_ids;
      } else {
        // ID'ler yoksa isimleri ID olarak kullan
        teacherIds = ogrenci.teacher_name.map((name, idx) => idx);
      }
    }
    // Eğer teacher_name yoksa ama teacher_ids varsa
    else if (ogrenci.teacher_ids && Array.isArray(ogrenci.teacher_ids) && ogrenci.teacher_ids.length > 0) {
      teacherIds = ogrenci.teacher_ids;
      teacherNames = ogrenci.teacher_ids.map(id => {
        if (teachers && Array.isArray(teachers)) {
          const teacher = teachers.find(t => t.id == id);
          if (teacher) {
            return teacher.adi || teacher.name || teacher.full_name || `Öğretmen #${id}`;
          }
        }
        // Teachers listesinde yoksa backend'den gelen teacher_name'de ara
        if (ogrenci.teacher_name && Array.isArray(ogrenci.teacher_name)) {
          return ogrenci.teacher_name[ogrenci.teacher_ids.indexOf(id)] || `Öğretmen #${id}`;
        }
        return `Öğretmen #${id}`;
      });
    }
    // Eğer teacher_ids yoksa, ogretmen_id veya ogretmen_ids field'larını kontrol et
    else if (ogrenci.ogretmen_id || ogrenci.ogretmen_ids) {
      // Tek ID mi yoksa array mi kontrol et
      const ogretmenIds = ogrenci.ogretmen_ids 
        ? (Array.isArray(ogrenci.ogretmen_ids) ? ogrenci.ogretmen_ids : [ogrenci.ogretmen_ids])
        : (ogrenci.ogretmen_id ? [ogrenci.ogretmen_id] : []);
      
      if (ogretmenIds.length > 0) {
        // ID'lerden isimleri bul
        teacherIds = ogretmenIds;
        teacherNames = ogretmenIds.map(id => {
          if (teachers && Array.isArray(teachers)) {
            const teacher = teachers.find(t => t.id == id); // == kullan çünkü string/number karışık olabilir
            if (teacher) {
              return teacher.adi || teacher.name || teacher.full_name || `Öğretmen #${id}`;
            }
          }
          return `Öğretmen #${id}`;
        });
      }
    }
    
    // Eğer ogretmen_id'den isim bulamadıysak, diğer field'ları kontrol et
    if (teacherNames.length === 0) {
      const possibleTeacherFields = [
        'teacher_name', 'teacher_names', 'teachers', 
        'ogretmenler', 'ogretmen', 'ogretmen_adi', 'ogretmen_isim',
        'instructor', 'instructors', 'teacher'
      ];
      
      for (const field of possibleTeacherFields) {
        const data = ogrenci[field];
        
        if (data === null || data === undefined) continue;
        
        if (Array.isArray(data) && data.length > 0) {
          teacherNames = data.map(item => {
            if (typeof item === 'string' && item.trim() !== '') {
              return item;
            }
            if (typeof item === 'object' && item !== null) {
              return item.name || item.adi || item.full_name || '';
            }
            if (typeof item === 'number') {
              // Sayı ise teachers listesinden bul
              if (teachers && Array.isArray(teachers)) {
                const teacher = teachers.find(t => t.id == item);
                if (teacher) return teacher.adi || teacher.name;
              }
              return String(item);
            }
            return '';
          }).filter(name => name && name.trim() !== '');
          
          if (teacherNames.length > 0) {
            teacherIds = data.map((item, idx) => {
              if (typeof item === 'object' && item !== null) {
                return item.id || item.teacher_id || idx;
              }
              if (typeof item === 'number') return item;
              return idx;
            });
            break;
          }
        } else if (typeof data === 'string' && data.trim() !== '') {
          teacherNames = [data];
          teacherIds = [0];
          break;
        }
      }
    }
    
    // Hala boşsa teacher_id veya teacher_ids field'larını tekrar kontrol et
    if (teacherNames.length === 0) {
      const idFields = ['teacher_ids', 'teacher_id'];
      for (const field of idFields) {
        if (ogrenci[field]) {
          const ids = Array.isArray(ogrenci[field]) ? ogrenci[field] : [ogrenci[field]];
          teacherIds = ids;
          teacherNames = ids.map(id => {
            if (teachers && Array.isArray(teachers)) {
              const teacher = teachers.find(t => t.id == id);
              if (teacher) {
                return teacher.adi || teacher.name || `Öğretmen #${id}`;
              }
            }
            return `Öğretmen #${id}`;
          });
          if (teacherNames.length > 0) break;
        }
      }
    }
    
    // Hiçbir öğretmen bilgisi yoksa boş döndür
    if (teacherNames.length === 0) {
      return [];
    }
    
    // Debug: Veriyi kontrol et
    if (ogrenci.id === 144 || ogrenci.id === 276) {
      console.log('Öğrenci:', ogrenci.name, 'ID:', ogrenci.id);
      console.log('Teacher IDs:', teacherIds);
      console.log('Teacher Names:', teacherNames);
      console.log('Teacher Last Lessons:', ogrenci.teacher_last_lessons);
    }
    
    return teacherNames.slice(0, 10).map((teacherName, index) => {
      // Öğretmen ID'sini bul - isimden ID'ye eşleştirme yap
      let teacherId = null;
      let dayCount = null;
      
      // Önce teacherIds array'inden ID'yi al
      if (Array.isArray(teacherIds) && teacherIds[index] !== undefined) {
        teacherId = teacherIds[index];
      }
      
      // Eğer teacherName bir ID ise (number string), onu kullan
      if (!teacherId && /^\d+$/.test(teacherName)) {
        teacherId = parseInt(teacherName);
      }
      
      // Eğer hala ID yoksa, teachers listesinden isimle ara
      if (!teacherId && teachers && Array.isArray(teachers)) {
        const foundTeacher = teachers.find(t => 
          t.adi === teacherName || 
          t.name === teacherName || 
          t.full_name === teacherName
        );
        if (foundTeacher) {
          teacherId = foundTeacher.id;
        }
      }
      
      // teacher_last_lessons'dan gün sayısını al
      if (ogrenci.teacher_last_lessons) {
        // Debug: Tüm teacher_last_lessons key'lerini kontrol et
        const lessonKeys = Object.keys(ogrenci.teacher_last_lessons);

        // Tüm kombinasyonları dene
        if (teacherId) {
          // Önce direkt ID ile dene
          dayCount = ogrenci.teacher_last_lessons[teacherId];

          // Bulamazsa string olarak dene
          if (dayCount === null || dayCount === undefined) {
            dayCount = ogrenci.teacher_last_lessons[String(teacherId)];
          }

          // Hala bulamazsa number olarak dene
          if (dayCount === null || dayCount === undefined) {
            dayCount = ogrenci.teacher_last_lessons[parseInt(teacherId)];
          }

          // Eğer hala bulunamadıysa, key'lerde ara
          if (dayCount === null || dayCount === undefined) {
            // teacherId'nin tüm varyasyonlarını kontrol et
            for (let key of lessonKeys) {
              if (key == teacherId || key === String(teacherId) || key == String(teacherId)) {
                dayCount = ogrenci.teacher_last_lessons[key];
                break;
              }
            }
          }
        }

        // Backend'ten gelen veri negatif sayı olarak geliyor, pozitife çevir
        if (dayCount !== null && dayCount !== undefined && !isNaN(dayCount)) {
          dayCount = Math.abs(dayCount);
        } else {
          dayCount = null;
        }
      }

      // teacher_status'dan status bilgisini al (0=Pasif, 1=Aktif)
      let teacherStatus = 1; // Varsayılan aktif
      const teacherStatusSource = ogrenci.teacher_status && typeof ogrenci.teacher_status === 'object'
        ? ogrenci.teacher_status
        : {};
      const teacherLessonsSource = ogrenci.teacher_last_lessons && typeof ogrenci.teacher_last_lessons === 'object'
        ? ogrenci.teacher_last_lessons
        : {};
      const hasTeacherLessonRecord = teacherId !== null &&
        teacherId !== undefined &&
        teacherId !== 0 && (
          Object.prototype.hasOwnProperty.call(teacherStatusSource, teacherId) ||
          Object.prototype.hasOwnProperty.call(teacherStatusSource, String(teacherId)) ||
          Object.prototype.hasOwnProperty.call(teacherLessonsSource, teacherId) ||
          Object.prototype.hasOwnProperty.call(teacherLessonsSource, String(teacherId))
        );

      if (hasTeacherLessonRecord) {
        if (teacherId) {
          // Önce direkt ID ile dene
          teacherStatus = teacherStatusSource[teacherId];

          // Bulamazsa string olarak dene
          if (teacherStatus === null || teacherStatus === undefined) {
            teacherStatus = teacherStatusSource[String(teacherId)];
          }

          // Hala bulamazsa number olarak dene
          if (teacherStatus === null || teacherStatus === undefined) {
            teacherStatus = teacherStatusSource[parseInt(teacherId)];
          }

          // Varsayılan değer
          if (teacherStatus === null || teacherStatus === undefined) {
            teacherStatus = 1; // Aktif
          }
        }
      }
      
      // Öğretmen ismini kontrol et ve gerekiyorsa düzenle  
      let displayName = String(teacherName); // Her zaman string olmasını sağla
      
      // Eğer isim "Öğretmen #ID" formatındaysa, ID'den gerçek ismi bulmaya çalış
      if (displayName && displayName.includes('#')) {
        // "#" işaretinden sonrasını al
        const idMatch = displayName.match(/#(\d+)/);
        if (idMatch) {
          const extractedId = parseInt(idMatch[1]);
          if (teachers && Array.isArray(teachers)) {
            const teacher = teachers.find(t => t.id == extractedId);
            if (teacher && teacher.adi) {
              displayName = teacher.adi;
            }
          }
        }
      }
      
      // Optimize edilmiş renk ve bilgi hesaplama
      let info = {
        name: displayName,
        id: `teacher-${index}`,
        dayCount: dayCount,
        status: teacherStatus,
        teacherId: teacherId,
        canToggleStatus: hasTeacherLessonRecord
      };
      
      if (dayCount === undefined || dayCount === null || isNaN(dayCount)) {
        Object.assign(info, {
          lastLessonInfo: ' Henüz ders yok',
          colorClass: 'bg-red-50 border-red-400',
          icon: '❌'
        });
      } else {
        // dayCount zaten pozitif olarak geliyor
        
        if (dayCount === 0) {
          Object.assign(info, {
            lastLessonInfo: ' Bugün',
            colorClass: 'bg-green-50 border-green-400',
            icon: '✓'
          });
        } else if (dayCount === 1) {
          Object.assign(info, {
            lastLessonInfo: ' Dün',
            colorClass: 'bg-emerald-50 border-emerald-400',
            icon: '✓'
          });
        } else if (dayCount <= 7) {
          Object.assign(info, {
            lastLessonInfo: ` ${dayCount} gün önce`,
            colorClass: 'bg-blue-50 border-blue-400',
            icon: '→'
          });
        } else if (dayCount <= 30) {
          Object.assign(info, {
            lastLessonInfo: ` ${dayCount} gün önce`,
            colorClass: 'bg-amber-50 border-amber-400',
            icon: '!'
          });
        } else {
          Object.assign(info, {
            lastLessonInfo: ` ${dayCount} gün önce`,
            colorClass: 'bg-red-50 border-red-400',
            icon: '✗'
          });
        }
      }
      
      return info;
    });
  }, [ogrenci, teachers, getTeacherNames]);

  // Optimized date formatter
  const formatDate = useCallback((dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return '-';
    }
  }, []);

  // Memoized values
  const regStatusBadgeClass = (ogrenci.registration_status === 0 || ogrenci.registration_status === 1)
    ? 'bg-green-100 text-green-800'
    : 'bg-yellow-100 text-yellow-800';

  const regStatusText = useMemo(() => ({
    0: 'Devam Ediyor',
    1: 'Derslere Başladı',
    2: 'Vazgeçti',
    3: 'Dersler Bitti',
    4: 'Ödeme Sorunu',
    5: 'Ara Verdi'
  }[ogrenci.registration_status] || '-'), [ogrenci.registration_status]);

  const balanceBadgeClass = ogrenci.kalan_bakiye >= 0 
    ? 'bg-green-100 text-green-800' 
    : 'bg-red-100 text-red-800';

  // Memoized handlers
  const handleCheckboxChange = useCallback(() => onSelectChange(ogrenci.id), [onSelectChange, ogrenci.id]);
  const handleToggle = useCallback(() => onToggleExpand(ogrenci.id), [onToggleExpand, ogrenci.id]);
  const handleEdit = useCallback(() => onEdit(ogrenci), [onEdit, ogrenci]);
  const handleDelete = useCallback(() => {
    if (window.confirm('Bu öğrenciyi silmek istediğinize emin misiniz?')) {
      onDelete(ogrenci.id);
    }
  }, [onDelete, ogrenci.id]);
  const handleShowLessons = useCallback(() => onShowPastLessons(ogrenci.id, ogrenci.name), [onShowPastLessons, ogrenci.id, ogrenci.name]);
  const handleShowPayments = useCallback(() => onShowPayments(ogrenci.id, ogrenci.name), [onShowPayments, ogrenci.id, ogrenci.name]);


  // Currency formatter
  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat('tr-TR', { 
      style: 'currency', 
      currency: 'TRY',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Math.abs(amount || 0));
  }, []);

  // Pasif öğrenciler için opacity değeri
  const isPassive = parseInt(ogrenci.status) === 0;
  
  // 14 günden fazla ödeme yapmamış mı kontrol et
  const isPaymentDelayed = ogrenci.gun_farki !== null && ogrenci.gun_farki !== undefined && ogrenci.gun_farki > 14;
  
  // Satır rengi belirleme
  const getRowClassName = () => {
    if (isPassive) {
      return isHovered ? 'bg-red-200 opacity-95' : 'bg-red-100 opacity-90';
    } else if (isPaymentDelayed) {
      return isHovered ? 'bg-orange-400' : 'bg-orange-300';
    } else {
      return isHovered ? 'bg-gray-50' : '';
    }
  };
  
  return (
    <>
      <tr 
        className={`transition-all duration-200 ${getRowClassName()}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <td className="px-2 py-2">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={handleCheckboxChange}
            className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
          />
        </td>
        <td className="px-2 py-2">
          <div className={`text-sm font-medium ${
            isPassive ? 'text-red-900 font-semibold' : 
            isPaymentDelayed ? 'text-gray-900 font-semibold' : 
            'text-gray-900'
          }`}>{ogrenci.name || '-'}</div>
        </td>
        <td className="px-2 py-2">
          <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded ${
            isPassive 
              ? 'bg-red-600 text-white' 
              : 'bg-green-100 text-green-800'
          }`}>
            {isPassive ? 'Pasif' : 'Aktif'}
          </span>
        </td>
        <td className="px-2 py-2 text-sm ${
          isPassive ? 'text-red-900 font-medium' : 
          isPaymentDelayed ? 'text-gray-800 font-medium' : 
          'text-gray-700'
        }">{ogrenci.email || '-'}</td>
        <td className="px-2 py-2">
          <div className={`text-sm ${
            isPassive ? 'text-red-900 font-medium' : 
            isPaymentDelayed ? 'text-gray-800 font-medium' : 
            'text-gray-700'
          }`}>{formatPhone(ogrenci.parent_phone)}</div>
        </td>
        <td className="px-2 py-2">
          <div className="text-sm font-medium">
            {Array.isArray(ogrenci.sinif) && ogrenci.sinif.length > 0 ? (
              <div className="flex flex-wrap gap-0.5">
                {ogrenci.sinif.slice(0, 3).map((sinif, index) => {
                  const displayText = sinif === 'Anaokulu' ? 'AO' :
                                     sinif === 'Mezun' ? 'Mzn' :
                                     sinif === 'Üniversite' ? 'Üni' :
                                     sinif;
                  return (
                    <span key={index} className="text-xs font-semibold">
                      {displayText}
                    </span>
                  );
                })}
                {ogrenci.sinif.length > 3 && (
                  <span className="text-xs text-gray-600">+{ogrenci.sinif.length - 3}</span>
                )}
              </div>
            ) : (
              <span className="text-xs">{ogrenci.sinif || '-'}</span>
            )}
          </div>
        </td>
        <td className="px-2 py-2">
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded ${regStatusBadgeClass}`}>
            {{0: 'Devam', 1: 'Başladı', 2: 'Vazgeçti', 3: 'Bitti', 4: 'Ödeme!', 5: 'Ara'}[ogrenci.registration_status] || '-'}
          </span>
        </td>
        <td className={`px-2 py-2 text-xs ${
          isPassive ? 'text-red-900 font-medium' : 'text-gray-700'
        }`}>
          {formatDate(ogrenci.registration_date)}
        </td>
        <td className={`px-2 py-2 text-xs ${
          isPassive ? 'text-red-900 font-medium' : 'text-gray-700'
        }`}>
          {formatDate(ogrenci.last_transaction_date)}
        </td>
        <td className="px-2 py-2">
          <div className="flex items-center justify-between">
            <div className={`text-sm flex-1 ${
              isPassive ? 'text-red-900 font-medium' : 'text-gray-700'
            }`}>{getSubjectNames(ogrenci.subjects)}</div>
            {ogrenci.subjects && ogrenci.subjects.length > 0 && (
              <button
                onClick={handleShowLessons}
                className="ml-2 px-2 py-1 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-md transition-colors flex items-center gap-1"
                title="Geçmiş Dersleri Görüntüle"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <span className="text-xs font-medium">Dersler</span>
              </button>
            )}
          </div>
        </td>
        <td className="px-2 py-2">
          {teacherInfos.length === 0 ? (
            <span className="text-gray-500 text-sm">-</span>
          ) : (
            <div className="space-y-1">
              {/* İlk 2 öğretmeni göster */}
              {(showAllTeachers ? teacherInfos : teacherInfos.slice(0, 2)).map((info) => (
                <TeacherBadge
                  key={info.id}
                  info={info}
                  onToggleStatus={(teacherId) => onToggleTeacherStatus && onToggleTeacherStatus(ogrenci.id, teacherId)}
                />
              ))}
              {/* Eğer 2'den fazla öğretmen varsa göster/gizle butonu */}
              {teacherInfos.length > 2 && (
                <button
                  onClick={() => setShowAllTeachers(!showAllTeachers)}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  {showAllTeachers ? '▲ Daha az göster' : `▼ ${teacherInfos.length - 2} öğretmen daha`}
                </button>
              )}
            </div>
          )}
        </td>
        <td className="px-2 py-2">
          <div className={`text-sm truncate max-w-[130px] ${
            isPassive ? 'text-red-900 font-medium' : 'text-gray-700'
          }`} title={ogrenci.aciklama}>
            {ogrenci.aciklama || '-'}
          </div>
        </td>
        <td className="px-2 py-2">
          <div className="space-y-1">
            <button
              onClick={() => onShowBalanceDetail(ogrenci.id, ogrenci.name)}
              className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${balanceBadgeClass} hover:opacity-80 transition-opacity cursor-pointer`}
              title="Detayları görmek için tıklayın"
            >
              {ogrenci.kalan_bakiye >= 0 ? '+' : ''}{ogrenci.kalan_bakiye || 0} TL
            </button>
            {ogrenci.gun_farki !== null && ogrenci.gun_farki !== undefined && (
              <div className={`text-[10px] ${
                ogrenci.gun_farki === 0 ? 'text-green-600' :
                ogrenci.gun_farki === 1 ? 'text-green-600' :
                ogrenci.gun_farki <= 7 ? 'text-blue-600' :
                ogrenci.gun_farki <= 30 ? 'text-yellow-600' :
                'text-red-600'
              }`}>
                {ogrenci.gun_farki === 0 ? 'Bugün' : 
                 ogrenci.gun_farki === 1 ? 'Dün' :
                 `${ogrenci.gun_farki} gün önce`}
              </div>
            )}
          </div>
        </td>
        <td className="px-2 py-2">
          <div className="flex items-center space-x-1">
            <button
              onClick={handleToggle}
              className="text-indigo-600 hover:text-indigo-800 transition-colors p-1 rounded hover:bg-indigo-50"
              title="Detaylar"
            >
              <svg className={`w-4 h-4 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <button
              onClick={handleEdit}
              className="text-blue-600 hover:text-blue-800 transition-colors p-1 rounded hover:bg-blue-50"
              title="Düzenle"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button
              onClick={handleShowLessons}
              className="text-purple-600 hover:text-purple-800 transition-colors p-1 rounded hover:bg-purple-50"
              title="Dersler"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </button>
            <button
              onClick={handleShowPayments}
              className="text-green-600 hover:text-green-800 transition-colors p-1 rounded hover:bg-green-50"
              title="Ödeme"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </button>
            <button
              onClick={handleDelete}
              className="text-red-600 hover:text-red-800 transition-colors p-1 rounded hover:bg-red-50"
              title="Sil"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
            <button
              onClick={() => window.handleLoginAsStudent(ogrenci.id)}
              className="text-purple-600 hover:text-purple-800 transition-colors p-1 rounded hover:bg-purple-50"
              title="Öğrenci Girişi"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </td>
      </tr>
      
      {/* Lazy loaded expanded details */}
      {isExpanded && <ExpandedDetails ogrenci={ogrenci} getCompanyName={getCompanyName} formatCurrency={formatCurrency} isPassive={isPassive} />}
    </>
  );
}, (prevProps, nextProps) => {
  // Optimize edilmiş shallow comparison
  return prevProps.ogrenci.id === nextProps.ogrenci.id &&
         prevProps.isSelected === nextProps.isSelected &&
         prevProps.isExpanded === nextProps.isExpanded &&
         prevProps.ogrenci.kalan_bakiye === nextProps.ogrenci.kalan_bakiye &&
         prevProps.ogrenci.teacher_last_lessons === nextProps.ogrenci.teacher_last_lessons &&
         prevProps.ogrenci.teacher_ids === nextProps.ogrenci.teacher_ids &&
         prevProps.ogrenci.teacher_name === nextProps.ogrenci.teacher_name &&
         prevProps.ogrenci.ogretmen_id === nextProps.ogrenci.ogretmen_id &&
         prevProps.ogrenci.ogretmen_ids === nextProps.ogrenci.ogretmen_ids &&
         prevProps.ogrenci.status === nextProps.ogrenci.status &&
         prevProps.ogrenci.registration_status === nextProps.ogrenci.registration_status;
});

// Expanded details component - lazy loaded
const ExpandedDetails = memo(({ ogrenci, getCompanyName, formatCurrency, isPassive }) => {
  // Tarih formatlama fonksiyonu
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return '-';
    }
  };
  
  // Telefon formatlama fonksiyonu
  const formatPhone = (phone) => {
    if (!phone) return '-';
    const numbers = phone.replace(/\D/g, '');
    if (numbers.length === 10) {
      return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)} ${numbers.slice(6, 8)} ${numbers.slice(8, 10)}`;
    } else if (numbers.length === 11 && numbers[0] === '0') {
      return `(${numbers.slice(1, 4)}) ${numbers.slice(4, 7)} ${numbers.slice(7, 9)} ${numbers.slice(9, 11)}`;
    }
    return phone;
  };
  
  return (
    <tr className={isPassive ? "bg-red-200 opacity-95" : "bg-gray-50"}>
      <td colSpan={14} className="px-6 py-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <h4 className="font-semibold text-gray-700 mb-2">Öğrenci Bilgileri</h4>
            <p className="text-sm"><span className="font-medium">Durum:</span> {ogrenci.status === 1 ? 'Aktif' : 'Pasif'}</p>
            <p className="text-sm"><span className="font-medium">Okul:</span> {ogrenci.okul || '-'}</p>
            <p className="text-sm"><span className="font-medium">İl:</span> {ogrenci.city || '-'}</p>
            <p className="text-sm"><span className="font-medium">İlçe:</span> {ogrenci.district || '-'}</p>
            <p className="text-sm"><span className="font-medium">Tam Adres:</span> {ogrenci.tam_adres || '-'}</p>
            <p className="text-sm"><span className="font-medium">TC Kimlik No:</span> {ogrenci.tc_kimlik_no || '-'}</p>
            <p className="text-sm"><span className="font-medium">Parola:</span> {ogrenci.psr || '-'}</p>
          </div>
          <div>
            <h4 className="font-semibold text-gray-700 mb-2">Veli Bilgileri</h4>
            <p className="text-sm"><span className="font-medium">Veli Adı:</span> {ogrenci.parent_name || '-'}</p>
            <p className="text-sm"><span className="font-medium">Veli Tel 1:</span> {formatPhone(ogrenci.parent_phone)}</p>
            {ogrenci.parent_phone2 && (
              <p className="text-sm"><span className="font-medium">Veli Tel 2:</span> {formatPhone(ogrenci.parent_phone2)}</p>
            )}
          </div>
          <div>
            <h4 className="font-semibold text-gray-700 mb-2">Kayıt Bilgileri</h4>
            <p className="text-sm"><span className="font-medium">Kayıt Tarihi:</span> {formatDate(ogrenci.registration_date)}</p>
            <p className="text-sm"><span className="font-medium">Son İşlem:</span> {formatDate(ogrenci.last_transaction_date)}</p>
            <p className="text-sm"><span className="font-medium">Şirket:</span> {getCompanyName(ogrenci.registered_system)}</p>
            <p className="text-sm"><span className="font-medium">Temsilci:</span> {ogrenci.representative || '-'}</p>
          </div>
        </div>
        
        {/* Finansal Özet */}
        <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200">
          <h4 className="font-semibold text-gray-700 mb-3">Finansal Özet</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-sm text-gray-600">Toplam Ders Ücreti</p>
              <p className="text-lg font-bold text-gray-800">
                {formatCurrency(ogrenci.toplam_ders_ucreti)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600">Toplam Ödeme</p>
              <p className="text-lg font-bold text-gray-800">
                {formatCurrency(ogrenci.toplam_odeme)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600">Kalan Bakiye</p>
              <p className={`text-lg font-bold ${
                ogrenci.kalan_bakiye >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {formatCurrency(ogrenci.kalan_bakiye)}
              </p>
            </div>
          </div>
        </div>
        
        {/* Açıklama */}
        {ogrenci.aciklama && (
          <div className="mt-4">
            <h4 className="font-semibold text-gray-700 mb-2">Açıklama</h4>
            <p className="text-sm text-gray-600">{ogrenci.aciklama}</p>
          </div>
        )}
      </td>
    </tr>
  );
});

OgrenciTableRow.displayName = 'OgrenciTableRow';
ExpandedDetails.displayName = 'ExpandedDetails';

export default OgrenciTableRow;
